import { Fragment, Suspense, useEffect } from 'react';
import { Shell } from './components/shell';
import { Loading } from './components/ui';
import { lazyRoute } from './lazy-route';
import { useAuth } from './state/auth';
import { navigate, usePath, useRouteKey } from './router';
import { canManageAdministration, canReviewFeedback, canUseUserFeedback } from './role-access';

const Login = lazyRoute(() => import('./screens/login'));
const Feed = lazyRoute(() => import('./screens/feed'));
const Dashboard = lazyRoute(() => import('./screens/dashboard'));
const Search = lazyRoute(() => import('./screens/search'));
const Nearby = lazyRoute(() => import('./screens/nearby'));
const Topics = lazyRoute(() => import('./screens/topics'));
const TopicDetail = lazyRoute(() => import('./screens/topic-detail'));
const Places = lazyRoute(() => import('./screens/places'));
const Notifications = lazyRoute(() => import('./screens/notifications'));
const Settings = lazyRoute(() => import('./screens/settings'));
const Feedback = lazyRoute(() => import('./screens/feedback'));
const Admin = lazyRoute(() => import('./screens/admin'));
const AdminFeedback = lazyRoute(() => import('./screens/admin-feedback'));

export function App() {
  const { user, loading } = useAuth();
  const path = usePath();
  const routeKey = useRouteKey();
  useEffect(() => {
    if (!loading && user && path === '/login') navigate('/', true);
    if (!loading && !user && path !== '/login') navigate('/login', true);
  }, [loading, Boolean(user), path]);
  if (loading) return <div className="splash"><span className="brand__mark brand__mark--large"><i /><i /></span><Loading label="Opening private desk" /></div>;
  if (!user) return <Suspense fallback={<div className="route-loading"><Loading /></div>}><Login /></Suspense>;

  let screen: React.ReactNode;
  if (path.startsWith('/topics/')) screen = <TopicDetail />;
  else switch (path) {
    case '/': screen = <Feed />; break;
    case '/my': screen = <Feed initialView="following" />; break;
    case '/dashboard': screen = <Dashboard />; break;
    case '/search': screen = <Search />; break;
    case '/nearby': screen = <Nearby />; break;
    case '/bookmarks': screen = <Feed bookmarks />; break;
    case '/topics': screen = <Topics />; break;
    case '/places': screen = <Places />; break;
    case '/notifications': screen = <Notifications />; break;
    case '/settings': screen = <Settings />; break;
    case '/feedback': screen = canUseUserFeedback(user) ? <Feedback /> : <Feed />; break;
    case '/admin/feedback': screen = canReviewFeedback(user) ? <AdminFeedback /> : <Feed />; break;
    case '/admin': screen = canManageAdministration(user) ? <Admin /> : <Feed />; break;
    default: screen = <Feed />;
  }
  return (
    <Suspense fallback={<div className="route-loading"><Loading /></div>}>
      <Shell><Fragment key={routeKey}>{screen}</Fragment></Shell>
    </Suspense>
  );
}
